import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mapOptionPolygon } from '../models/interface/mapOptionPolygon.interface';
import { PasswordCheckerRequestShared } from '../models/interface/passwordCheckerRequest.interface';
import { PasswordCheckerShared } from '../models/interface/passwordChecker.interface';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { CountryShared } from '../models/interface/country.interface';
import { ItemNumberToFilterShared } from '../models/interface/itemNumberToFilter.interface';
import { OrderDateShared } from '../models/interface/orderDate.interface';
import { CityShared } from '../models/interface/cityShared.interface';
import { OrderType } from '../models/interface/orderType.enum';
import { OrderDetailShared } from '../models/interface/orderDetail.interface';
import { WayToPay } from '../models/interface/wayToPay.enum';
import { sharedPointsContants } from '../sharedConstants/points.constants';
import { environment } from 'src/environments/environment';
import moment from 'moment';
import 'moment-timezone';
import { MuMapAtlasService } from '../sharedModules/designSystem/mu-atlas/services/mu-map-atlas.service';

export type PromiseFactory<T> = () => Promise<T>;

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  optionsGoogleMapsPolygon: mapOptionPolygon;

  constructor(private muMapAtlasService: MuMapAtlasService) {
    /** */
  }

  /**
   * Create a Promise of the last value of observable
   * @param  {Observable<T>} observable
   * @param  {(t:T)=>boolean} checkValue
   * @returns PromiseFactory
   */
  promiseFactory<T>(observable: Observable<T>, checkValue: (t: T) => boolean): PromiseFactory<T> {
    let promise: Promise<T> = null;
    let resolveValue = null;
    let resolved = false;
    const createPromise = () => {
      promise = new Promise<T>(resolve => {
        resolveValue = (data: T) => {
          if (data && checkValue(data)) {
            resolve(data);
            resolved = true;
          }
        };
      });
    };
    createPromise();
    observable.subscribe(data => {
      if (resolved) {
        resolved = false;
        createPromise();
      }
      resolveValue(data);
    });
    return () => {
      return promise;
    };
  }

  /**
   * method to format currency
   * @param {string} value
   * @param {CountryShared} country
   * @return {string} Response formatted
   */
  formatCurrency(value: string | number, country?: CountryShared): string {

    value = this.deleteInvalidsDots(value?.toString());

    if (!country) {
      country = environment.countries.find(x => x.geoName === localStorage.getItem('location'));
    }
    if ((value + '').includes('$')) {
      return value + '';
    }

    value = value?.toString();
    value = Number(!country?.removeDecimal ? value?.replace(',', '.') : value);

    let response = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);

    if (!country?.holdZeroDecimal) {
      response = response.replace('.00', '');
    }

    if (country?.removeDecimal) {
      response = response.split('.')[0];
    }

    return response;
  }


  /**
   * Delete the invalid dots on string
   * @param value
   * @returns string
   */
   deleteInvalidsDots(value: any) : string{
    if(value){
      value = value.toString();
      let regex = /[.]/g;
// eslint-disable-next-line no-unsafe-optional-chaining
      let dotsFounded = [...value?.matchAll(regex)];
      if (dotsFounded.length > 1) {
        return this.insertCharacter(value?.replace(regex,''), dotsFounded[0].index, '.');
      }else{
        return value;
      }
    }else{
      return value
    }
  }

  /**
   * insert a character at the given position
   * @param str
   * @param index
   * @param character
   * @returns new with the character inserted
   */
  insertCharacter(str: any, index: number, character: string): string {
    return str.slice(0, index) + character + (str).slice(index);
  }

  /**
   * Number string to format
   */
  formatPhoneNumber(phoneNumberString) {
    let cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return match[1] + ' ' + match[2] + ' ' + match[3];
    }
    return null;
  }

  /**
   * pre format currency holding numbers, dots and commas
   * @param value
   * @returns
   */
  cleanCurrency(value: string = ''): string {
    return value.toString().replace(/[^0-9.,]/g, '');
  }

  /**
   * preformat only numbers
   * @param value
   * @returns
   */
  cleanNumeric(value: string = ''): string {
    return value.toString().replace(/\D/g, '');
  }

  /**
   * method to format date updated from ws
   * @param {string} value
   * @param {boolean} parse
   * returns {OrderDate} response
   */
  getDateAndTime(value: string, parse?: boolean): OrderDateShared {
    const date = new Date(parse ? Date.parse(value) : value);
    const startDate = `${date.getFullYear()}-${this.twoDigit(date.getMonth() + 1)}-${this.twoDigit(date.getDate())}`;
    const startTime = `${this.twoDigit(date.getHours())}:${this.twoDigit(date.getMinutes())}:${this.twoDigit(date.getSeconds())}`;
    return {
      startDate,
      startTime,
    };
  }

  /**
   * method to know if a input is a number
   * @param {string} value Value to validate
   * @return {boolean} response True or false if Input is number
   */
  isNumeric(value: string = ''): boolean {
    value = value?.replace(',', '')?.replace('.', '');
    return /^[0-9 ]+$/.test(value?.replace(/ /g, ''));
  }

  /**
   * method to know if a input is a decimal
   * @param {string} value Value to validate
   * @return {boolean} response True or false if Input is number
   */
  isNumericDecimal(value: string): boolean {
// eslint-disable-next-line no-useless-escape
    return value === '.' || /^(\d+(\,\d\d?)?)+$/.test(value?.replace(/ /g, ''));
  }

  /**
   * method to get format hours for schedule
   * @param {string} startHour
   * @param {string} endHour
   * @param {string} to?
   * @return {string} response
   */
  getHoursForSchedule(startHour: string, endHour: string, to?: string): string {
    startHour = (startHour + '').length < 4 ? '0' + startHour : startHour;
    endHour = (endHour + '').length < 4 ? '0' + endHour : endHour;
    return startHour.slice(0, 2) + ':' + startHour.slice(2, 4) + ' ' + (to || '-') + ' ' + endHour.slice(0, 2) + ':' + endHour.slice(2, 4);
  }

  /**
   * method to format time with time zone from browser
   * @param {string} date
   * @param {string} time
   * @param {string} currentTimeZone
   * @return {OrderDate} response
   */
  formatDateTimeWithTimeZone(date: string, time: string, currentTimeZone: string): OrderDateShared {
    if (time.length === 7) {
      time = this.twoDigit(time);
    }
    const timeNow = moment.tz(`${date} ${time}`, 'America/Bogota');
    let newDate = timeNow.clone().tz(Intl.DateTimeFormat().resolvedOptions().timeZone);
    try {

      return this.getDateAndTime(
        `${newDate.format('YYYY-MM-DDTHH:mm:ss')}`,
        true
      );
    } catch (e) {
      return this.getDateAndTime(`${newDate}`);
    }
  }

  /**
   * method to find city of stop
   * @param {string} cityName
   * @param {City[]} cityListBase
   * @return {City} response
   */
  findCityInList(cityName: string, cityListBase: CityShared[]): CityShared {
    cityName = this.cleanString(cityName);
    return cityListBase.filter(
      x => this.cleanString(x.geoName) === cityName || this.cleanString(x.name) === cityName || this.cleanString(x.name).includes(cityName)
    )[0];
  }

  /**
   * method to get only letters from string
   * @param {string} value
   * @return {string} response
   */
  cleanString(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * method to add second digit to number
   * @param {number} value Input
   * @return {string} response
   */
  twoDigit(value: number | string): string {
    return (parseInt(value.toString()) < 10 ? '0' : '') + value.toString();
  }

  /**
   *   method to change input type between text and password for eye icon
   *   @param {Event} event An Event with html element data
   *   @param {string} id html id
   */
  changeInputTypePassword(event: Event, id: string): void | boolean {
    if (!event) {
      return environment.guardClause;
    }
    const icon = (event.target as HTMLInputElement).classList;
    const password = document.getElementById(id) as HTMLInputElement;
    password.type = password.type === 'password' ? 'text' : 'password';

    if (icon.contains('fa-eye')) {
      icon.remove('fa-eye');
      icon.add('fa-eye-slash');
      return true;
    }

    icon.remove('fa-eye-slash');
    icon.add('fa-eye');
  }

  /**
   * method to clean declared value
   * @param {string} value Declared value
   */
  unformatCurrencyValue(value: string = ''): string {
    if (value === '.') {
      value = '0';
    }
    return this.cleanCurrency(value.toString())?.replace(/,/g, '');
  }

  /**
   * method to format date updated from ws
   * return {WebSocketDate} response
   */
  getTime(value: string): string {
    const date = new Date(value);
    return `${this.twoDigit(date.getHours())}:${this.twoDigit(date.getMinutes())}`;
  }

  /**
   * get time with am pm
   * @param value
   * @returns
   */
  getTimeAMPM(value: string): string {
    const time = value.match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [value];
    const timeArray = time.slice(1);
    timeArray[5] = +timeArray[0] < 12 ? ' am' : ' pm';
    timeArray[0] = (+timeArray[0] % 12 || 12) + '';
    return timeArray.join('');
  }

  /**
   * format polygon
   * @param multipolygonWKT
   * @returns
   */
  formatPolygon(multipolygonWKT: string): any {
    multipolygonWKT = multipolygonWKT.replace('POLYGON((', '');
    multipolygonWKT = multipolygonWKT.replace('))', '');
    const coordinates = multipolygonWKT.split(',');
    const path = [];
    coordinates.forEach(element => {
      const coordinate = element.split(' ');
      path.push({ lng: Number(coordinate[0]), lat: Number(coordinate[1]) });
    });
    this.optionsGoogleMapsPolygon = sharedPointsContants.optionsGoogleMapsPolygon;
    const mapOption = {
      paths: path,
      strokeColor: this.optionsGoogleMapsPolygon.strokeColor,
      strokeOpacity: this.optionsGoogleMapsPolygon.strokeOpacity,
      strokeWeight: this.optionsGoogleMapsPolygon.strokeWeight,
      fillColor: this.optionsGoogleMapsPolygon.fillColor,
      fillOpacity: this.optionsGoogleMapsPolygon.fillOpacity,
    };
    return this.muMapAtlasService.createPolygon(mapOption);
  }

  /**
   * method to know status of check password
   * @param pwd password
   * @param checkParams
   */
  validatePassword(pwd: string, checkParams?: PasswordCheckerRequestShared): PasswordCheckerShared {
    const params = { ...{ length: 8, withSymbol: true, withUpper: true, withLower: true, withNumber: true }, ...(checkParams || {}) };
// eslint-disable-next-line no-useless-escape
    const symbolRegexp = /[,\.\?\$#_]/;
    const lowerRegexp = /[a-z]/;
    const upperRegexp = /[A-Z]/;
    const numberRegexp = /\d/;
    let valid = true;
    const symbol = params.withSymbol ? symbolRegexp.test(pwd) : true;
    const upper = params.withUpper ? upperRegexp.test(pwd) : true;
    const lower = params.withLower ? lowerRegexp.test(pwd) : true;
    const passwordNumber = params.withNumber ? numberRegexp.test(pwd) : true;
    const length = pwd.length >= params.length;
    const score = (symbol ? 1 : 0) + (upper ? 1 : 0) + (lower ? 1 : 0) + (passwordNumber ? 1 : 0) + (length ? 1 : 0);
    valid = this.isValidPassword(params, symbol, upper, lower, passwordNumber);

    return {
      symbol,
      upper,
      lower,
      passwordNumber,
      length,
      valid,
      strength: Math.round((score / 5) * 100),
    };
  }

  /**
   * is valid password
   * @param {[key: string]: any} params
   * @param {boolean} symbol
   * @param {boolean} upper
   * @param {boolean} lower
   * @param {boolean} passwordNumber
   * @returns {boolean} response
   */
  isValidPassword(params: { [key: string]: any }, symbol: boolean, upper: boolean, lower: boolean, passwordNumber: boolean): boolean {
    if (
      (params.withSymbol && !symbol) ||
      (params.withUpper && !upper) ||
      (params.withLower && !lower) ||
      (params.withNumber && !passwordNumber) ||
      !length
    ) {
      return false;
    }
    return true;
  }

  /**
   * get current date for selected country
   * @param {string} field
   * @param {FormGroup} formData
   * @param {CountryShared} country
   * @param {string} date
   * @retun {Date} minDate
   */
  updateStartDateTimeField(field: string, formData: UntypedFormGroup, country: CountryShared, date?: string): Date {
    const baseDate = this.getDateAndTime(date || new Date().toString());
    const formatDate = this.formatDateTimeWithTimeZone(baseDate.startDate, baseDate.startTime, country.timeZone);
    const minDate = new Date(formatDate.startDate + ' ' + formatDate.startTime);
    minDate.setMinutes(minDate.getMinutes() + (15 - (minDate.getMinutes() % 15)));
    formData?.get(field).setValidators([Validators.required]);
    formData?.get(field).updateValueAndValidity();
    formData?.get(field).setValue(minDate);
    return minDate;
  }

  /**
   * find city in city list for filter
   * @param {ItemNumberToFilterShared[]} cityList
   * @param {string} cityName
   * @returns {ItemNumberToFilterShared[]} city
   */
  findCityInFilterList(cityList: ItemNumberToFilterShared[], cityName: string): ItemNumberToFilterShared {
    let city = cityList.find(x => {
      return x.extra === cityName || x.label.toLowerCase().trim() === cityName.toLowerCase().trim();
    });
    if (!city) {
      city = cityList.find(x => {
        return x.label.toLowerCase().trim().includes(cityName.toLowerCase().trim());
      });
    }
    return city;
  }

  /**
   * is order type prescription with round trip
   */
  isPrescriptionRoundTrip(orderDetail: OrderDetailShared): boolean {
    return (
      orderDetail.orderType === OrderType.PRESCRIPTION &&
      (orderDetail.orderStops[0].paymentTypeId === WayToPay.CASH_ROUND_TRIP + '' ||
        orderDetail.orderStops[0].paymentTypeId === WayToPay.DATAPHONE + '')
    );
  }

  /**
   * return capitalize string
   * @param value value to capitalize
   */
  capitalize(value: string): string {
    const result = value.toLowerCase();
    return `${result.charAt(0).toUpperCase()}${result.substring(1)}`;
  }

  /**
   * serialize form
   * @param {FormGroup} formData
   * @returns {{[key: string]: any}}
   */
  serializeForm(formData: UntypedFormGroup): { [key: string]: any } {
    const response: { [key: string]: any } = {};
    Object.keys(formData.controls)
      .filter(x => x !== 'isInvalidForm')
      .forEach((key: string) => {
        response[key] = formData.get(key).value;
      });
    return response;
  }

  /**
   * sleep function
   * @param {number} timeValue
   */
  async muSleep(timeValue: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, timeValue));
  }
}
